import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { DateValue } from 'react-aria-components';
import type { RangeValue } from '@react-types/shared';
import { parseAbsoluteToLocal } from '@internationalized/date';

const formatDate = (date: DateValue) =>
  `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;

export const useNotificationFilter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const startDateParam = searchParams.get('created_at_after');
  const endDateParam = searchParams.get('created_at_before');

  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
    null
  );

  // Sync date range from query params
  useEffect(() => {
    if (startDateParam && endDateParam) {
      try {
        const start = parseAbsoluteToLocal(startDateParam);
        const end = parseAbsoluteToLocal(endDateParam);
        setDateRange({ start, end });
      } catch (error) {
        console.warn('Invalid date range in query params', error);
      }
    }
  }, [startDateParam, endDateParam]);

  const updateQuery = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(window.location.search);

      Object.entries(updates).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });

      navigate(
        {
          pathname: window.location.pathname,
          search: params.toString(),
        },
        { replace: true }
      );
    },
    [navigate]
  );
  const handleUnreadFilter = useCallback(
    () => updateQuery({ is_read: 'false' }),
    [updateQuery]
  );
  const handleAllFilter = useCallback(
    () => updateQuery({ is_read: null }),
    [updateQuery]
  );

  const handleDateApply = useCallback(() => {
    if (dateRange?.start && dateRange?.end) {
      updateQuery({
        created_at_after: formatDate(dateRange.start),
        created_at_before: formatDate(dateRange.end),
      });
    }
  }, [dateRange, updateQuery]);

  return {
    setDateRange,
    handleDateApply,
    handleUnreadFilter,
    handleAllFilter,
    dateRange,
  };
};
